import LiquidText, { liquidTone } from './LiquidText';
import { OrcamentoIcon } from './OrcamentoUI';
import { LiquidDialog } from './LiquidUI';
import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { abrirFicheiroDaResposta } from '../utils/abrirFicheiro';

/* ============================================================
   A LUPA — procura global do Sistema Integrado

   Para o dia de auditoria: a auditora pede um documento pelo nome
   que lhe ocorre e a resposta aparece mesmo sem acentos, noutra
   caixa, por palavras soltas ou com gralhas (unaccent + pg_trgm
   no servidor). Um clique abre o PDF quando existe.
   ============================================================ */

export default function ProcuraSgi({ onFechar }) {
    const [q, setQ] = useState('');
    const [resultados, setResultados] = useState([]);
    const [aProcurar, setAProcurar] = useState(false);
    const [abrindo, setAbrindo] = useState(null);
    const caixa = useRef(null);
    const temporizador = useRef(null);

    useEffect(() => { caixa.current?.focus(); }, []);

    useEffect(() => {
        const esc = (e) => { if (e.key === 'Escape') onFechar(); };
        window.addEventListener('keydown', esc);
        return () => window.removeEventListener('keydown', esc);
    }, [onFechar]);

    useEffect(() => {
        clearTimeout(temporizador.current);
        if (q.trim().length < 2) { setResultados([]); return; }
        temporizador.current = setTimeout(async () => {
            setAProcurar(true);
            try {
                const r = await api.get('/procura-sgi', { params: { q } });
                setResultados(r.data || []);
            } catch { setResultados([]); }
            finally { setAProcurar(false); }
        }, 350);
        return () => clearTimeout(temporizador.current);
    }, [q]);

    const abrir = async (item) => {
        setAbrindo(`${item.fonte}-${item.id}`);
        try {
            const r = await api.get(`/procura-sgi/ficheiro/${item.fonte}/${item.id}`, { responseType: 'blob' });
            abrirFicheiroDaResposta(r, item.titulo || '');
        } catch { alert('Erro ao abrir o ficheiro.'); }
        finally { setAbrindo(null); }
    };

    return (
        <LiquidDialog label="Procurar no Sistema Integrado" data-sgi-dialog="true" onClose={onFechar} style={overlay} onClick={onFechar}>
            <div style={{ ...modal, maxWidth: 760 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                    <OrcamentoIcon name="search" fallback="🔍" />
                    <input aria-label="Procurar no Sistema Integrado" ref={caixa} value={q} onChange={e => setQ(e.target.value)}
                        placeholder="O que a auditora pediu? — ex.: ata revisao, fds soudaflex, procedimento compras…"
                        style={{ flex: 1, padding: '10px 14px', border: '2px solid #3182ce', borderRadius: 10, fontSize: '1rem' }} />
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onFechar} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                <div style={{ maxHeight: '62vh', overflowY: 'auto' }}>
                    {q.trim().length < 2 ? (
                        <p style={{ color: '#94a3b8', fontSize: '0.86rem', padding: '18px 4px' }}>
                            Escreve como souberes: sem acentos, com gralhas, por palavras soltas.
                            A procura cobre documentos, evidências, certificados, anexos, NC,
                            formação, equipamentos e o resto do SGI — e vai <strong>dentro dos
                                documentos</strong>, não só aos títulos. Experimenta «bacia de retenção»
                            ou «atmosferas explosivas».
                        </p>
                    ) : aProcurar && resultados.length === 0 ? (
                        <p style={{ color: '#718096', padding: '14px 4px' }}>A procurar…</p>
                    ) : resultados.length === 0 ? (
                        <p style={{ color: '#94a3b8', padding: '14px 4px' }}>
                            Nada encontrado para «{q}». Tenta menos palavras ou outro termo.
                        </p>
                    ) : resultados.map(item => (
                        <div key={`${item.fonte}-${item.id}`}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 12, padding: '9px 10px',
                                borderBottom: '1px solid #f1f5f9', borderRadius: 8,
                            }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}><LiquidText>{item.titulo}</LiquidText></div>
                                <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: 2 }}>
                                    <span data-liquid-tone={liquidTone('#eff6ff')} data-liquid-badge="true" style={{
                                        background: '#eff6ff', color: '#1d4ed8', padding: '1px 8px',
                                        borderRadius: 10, fontWeight: 600, marginRight: 6,
                                    }}>{item.modulo}</span>
                                    {item.detalhe}
                                    {item.achado_no_conteudo && (
                                        <span style={{ color: '#166534', fontWeight: 600 }}> · dentro do documento</span>
                                    )}
                                    {!item.achado_no_conteudo && Number(item.pontuacao) < 0.9 && (
                                        <span style={{ color: '#b45309' }}> · correspondência aproximada</span>
                                    )}
                                </div>
                                {/* Onde está: o documento e a página. Sem isto, "encontrado no
                                    conteúdo" obriga a abrir um PDF de 100 páginas à procura. */}
                                {item.achado_no_conteudo && (
                                    <div data-liquid-notice="true" data-liquid-tone={liquidTone('#f0fdf4')} style={{
                                        marginTop: 5, padding: '7px 10px', background: '#f0fdf4',
                                        borderLeft: '3px solid #16a34a', borderRadius: 4,
                                    }}>
                                        <div style={{
                                            fontSize: '0.76rem', fontWeight: 700, color: '#166534',
                                            marginBottom: item.trecho ? 4 : 0,
                                        }}>
                                            Encontrado em: {item.nome_ficheiro || item.titulo}
                                            {item.pagina
                                                ? ` — ${item.rotulo_seccao || 'página'} ${item.pagina}` +
                                                (item.total_paginas > 1 ? ` de ${item.total_paginas}` : '')
                                                : ''}
                                        </div>
                                        {item.trecho && (
                                            <div style={{ fontSize: '0.78rem', color: '#334155', lineHeight: 1.45 }}>
                                                …{item.trecho.trim()}…
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {item.tem_ficheiro ? (
                                <button data-liquid-primary="true" onClick={() => abrir(item)} disabled={abrindo === `${item.fonte}-${item.id}`}
                                    style={{ ...btnPri, whiteSpace: 'nowrap', opacity: abrindo === `${item.fonte}-${item.id}` ? 0.6 : 1 }}>
                                    <LiquidText>{abrindo === `${item.fonte}-${item.id}` ? 'A abrir…' : '📄 Abrir'}</LiquidText>
                                </button>
                            ) : (
                                <span style={{ fontSize: '0.74rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>registo no módulo</span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </LiquidDialog>
    );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1200, padding: '60px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 18, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.15rem', cursor: 'pointer', color: '#64748b' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
